import { useMemo } from 'react'
import { AutoComplete } from '@microservices/wiskey-react-components'
import { Box, Grid, TextField, Typography } from '@mui/material'
import { grey } from '@mui/material/colors'

import { ScrollableLine } from '@components/ScrollableLine'

import { generalFieldStylesByMode, getAutoCompletePopperSxByDisplayMethod } from '@helpers'
import {
  CriteriaType,
  FIELD_VALUE_FORMAT,
  MAX_COUNT_UNPINNED_FILTERS,
  ValueAddType,
  ValueType,
} from '@constants'
import { AutocompleteOption } from '@types'

import { useSearchAssistantContext } from '../../context'

import { SearchAssistantInputValue } from './SearchAssistantInputValue'

interface SearchAssistantSelectOptionProps {
  options?: CriteriaType[]
  assistantCriteria: CriteriaType
  onChangeCriteriaValue: (value: ValueAddType) => void
  currentCriteriaNames: string[]
  onChange: (value: AutocompleteOption) => void
  onChangeInputValue: (value: ValueType) => void
  disabled?: boolean
  width: number
}

const SearchAssistantSelectOption: React.FC<SearchAssistantSelectOptionProps> = props => {
  const {
    options,
    assistantCriteria,
    onChangeCriteriaValue,
    currentCriteriaNames,
    onChange,
    onChangeInputValue,
    disabled = false,
    width,
  } = props

  const context = useSearchAssistantContext('SearchAssistantSelectOption')

  const autocompleteOptions = useMemo(
    () =>
      options
        ?.filter(
          criteria =>
            !currentCriteriaNames.includes(criteria.title) ||
            criteria.title === assistantCriteria?.title
        )
        .map((criteria, i) => ({ label: criteria.title, id: i + MAX_COUNT_UNPINNED_FILTERS })),
    [options, currentCriteriaNames, assistantCriteria]
  )

  return (
    <Grid item>
      <Grid container alignItems={'center'} flexWrap={'nowrap'}>
        {
          <Grid item minWidth={300} width={300}>
            {autocompleteOptions ? (
              <AutoComplete
                disableClearable
                fullWidth
                disabled={disabled}
                isOptionEqualToValue={(option, value) => option.label === value.label}
                options={autocompleteOptions}
                size='small'
                componentsProps={{
                  popper: {
                    sx: {
                      zIndex: theme => theme.zIndex.tooltip,
                      ...getAutoCompletePopperSxByDisplayMethod(context.isInDialogWindow),
                    },
                  },
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    sx={theme => ({
                      ...generalFieldStylesByMode(
                        theme,
                        undefined,
                        undefined,
                        undefined,
                        context.isInDialogWindow
                      ),
                    })}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...restProps } = props

                  return (
                    <li {...restProps} key={key} title={option.label}>
                      <Box
                        sx={{ overflowX: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {option.label}
                      </Box>
                    </li>
                  )
                }}
                value={
                  assistantCriteria
                    ? { id: assistantCriteria.codeCriteria, label: assistantCriteria.title }
                    : null
                }
                // eslint-disable-next-line react/jsx-handler-names
                onChange={onChange}
              />
            ) : (
              <Typography color={theme => (disabled ? grey[600] : theme.palette.color.field)}>
                {assistantCriteria?.title}
              </Typography>
            )}
          </Grid>
        }
        <Grid item flex={1}>
          <ScrollableLine width={width - 295}>
            {assistantCriteria?.storedValues?.map(value => {
              const asCheckbox =
                value.valueFormat === FIELD_VALUE_FORMAT.NUMBER ||
                value.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN

              return (
                <Box
                  key={value.id}
                  sx={
                    asCheckbox
                      ? {}
                      : {
                          minWidth: '200px',
                          width: '100%',
                        }
                  }
                >
                  <SearchAssistantInputValue
                    addValue={onChangeCriteriaValue}
                    codeCriteria={assistantCriteria.codeCriteria}
                    currentValue={value}
                    disabled={disabled}
                    fieldType={value.fieldType}
                    inputFormat={value.formatDate}
                    linkedObjectCode={value.linkedObjectCode}
                    objectValue={value.objectValue}
                    // eslint-disable-next-line react/jsx-handler-names
                    onChange={onChangeInputValue}
                  />
                </Box>
              )
            })}
          </ScrollableLine>
        </Grid>
      </Grid>
    </Grid>
  )
}

SearchAssistantSelectOption.displayName = 'SearchAssistantSelectOption'

export { SearchAssistantSelectOption }
